import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import Cookies from "js-cookie";
import { getUserEmail } from "shared-frontend/redux/selectors/user/data/profile";
import { prepareInternalRequest, doRequest } from "shared-frontend/functions/api";
import {
	NEWSLETTER_NOTIFICATION_COOKIE,
	NEWSLETTER_COOKIE_EXPIRY_DAYS,
	ENABLE_NEWSLETTER_NOTIFICATION,
} from "../../config/newsletterNotificationConfig";

/**
 * Hook that determines whether the user is eligible to see the newsletter notification.
 *
 * Eligibility requires:
 * - ENABLE_NEWSLETTER_NOTIFICATION is true
 * - User has not dismissed the notification (cookie)
 * - User has an email address
 * - User is NOT already subscribed to the newsletter (checked via API)
 *
 * On API error, assumes subscribed (don't show notification).
 *
 * @returns {{ isEligible: boolean, isLoading: boolean, dismiss: Function, email: string }} Newsletter eligibility state.
 */
const useNewsletterEligibility = () => {
	const [ isDismissed, setIsDismissed ] = useState( () => !! Cookies.get( NEWSLETTER_NOTIFICATION_COOKIE ) );
	const [ isSubscribed, setIsSubscribed ] = useState( null );
	const [ isLoading, setIsLoading ] = useState( true );

	const email = useSelector( getUserEmail );

	useEffect( () => {
		// Skip API call if disabled, already dismissed, or no email yet.
		if ( ! ENABLE_NEWSLETTER_NOTIFICATION || isDismissed || ! email ) {
			setIsLoading( false );
			return;
		}

		let isCancelled = false;
		setIsLoading( true );
		setIsSubscribed( null );

		const request = prepareInternalRequest(
			"Mailing-list/myNewsletterStatus",
			"GET",
		);

		const fetchSubscriptionStatus = async() => {
			try {
				const data = await doRequest( request );
				if ( ! isCancelled ) {
					setIsSubscribed( data.status === "subscribed" );
				}
			} catch {
				// On error, assume subscribed (don't show notification).
				if ( ! isCancelled ) {
					setIsSubscribed( true );
				}
			} finally {
				if ( ! isCancelled ) {
					setIsLoading( false );
				}
			}
		};

		fetchSubscriptionStatus();
		return () => {
			isCancelled = true;
		};
	}, [ email, isDismissed ] );

	const dismiss = useCallback( () => {
		Cookies.set( NEWSLETTER_NOTIFICATION_COOKIE, "true", { expires: NEWSLETTER_COOKIE_EXPIRY_DAYS } );
		setIsDismissed( true );
	}, [] );

	const isEligible = ENABLE_NEWSLETTER_NOTIFICATION && ! isDismissed && ! isLoading && isSubscribed === false;

	return { isEligible, isLoading, dismiss, email };
};

export default useNewsletterEligibility;
